import { usePrevious } from '@react-hookz/web';
import type {
  ElementMissingBehavior,
  TooltipWalkthroughStep,
} from '@unifyapps/defs/types/page/walkthrough';
import { useEffect, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useGetWalkthroughManagerStore } from '../../../../stores/WalkthroughManagerStore';
import { useWalkthroughOrchestrator } from '../../../hooks/useWalkthroughOrchestrator';
import { TargetElementFinder } from '../../../services/targetElementFinder';

/**
 * Hook that delegates to the TargetElementFinder service.
 * React just subscribes to results - retry logic lives outside component lifecycle.
 */
export function useWalkthroughTargetElement(
  step: Pick<
    TooltipWalkthroughStep,
    | 'id'
    | 'targetBlockId'
    | 'targetElementQuery'
    | 'targetType'
    | 'customCSSSelector'
    | 'waitUntilElementAppears'
    | 'waitUntilElementAppearsDelay'
  >,
  containerEl: HTMLElement | null,
  elementMissingBehavior?: ElementMissingBehavior,
  isBuilderOrPreview = false,
  onElementMissing?: () => void,
) {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [elementFound, setElementFound] = useState(false);
  const [targetElementFinder, _] = useState<TargetElementFinder>(() => new TargetElementFinder());
  const { moveStep, stopWalkthrough } = useWalkthroughOrchestrator();
  const { getWalkthroughManagerStore } = useGetWalkthroughManagerStore();
  // Track previous step ID to detect changes
  const previousStep = usePrevious(step);

  // Effect: start finder when step changes
  useEffect(() => {
    if (!containerEl) {
      setTargetElement(null);
      setElementFound(false);
      return;
    }

    if (isEqual(step, previousStep)) {
      return;
    }

    // Only start finder if step actually changed
    setTargetElement(null);
    setElementFound(false);

    // Start the finder service
    targetElementFinder.start({
      step,
      containerEl,
      getStore: getWalkthroughManagerStore,
      onElementFound: (element) => {
        setTargetElement(element);
        setElementFound(true);
      },
      onElementMissing: () => {
        setTargetElement(null);
        setElementFound(false);
        onElementMissing?.();
      },
      elementMissingBehavior,
      isBuilderOrPreview,
      moveStep,
      stopWalkthrough,
    });

    // Cleanup: stop finder when component unmounts or step changes
    return () => {
      targetElementFinder.effectCleanup();
    };
  }, [
    step,
    previousStep,
    containerEl,
    elementMissingBehavior,
    isBuilderOrPreview,
    getWalkthroughManagerStore,
    onElementMissing,
    moveStep,
    stopWalkthrough,
    targetElementFinder,
  ]);

  return {
    targetElement,
    elementFound,
  };
}
